// v19.29 — Public per-beat detail page.
// Route: /beats/:artistSlug/:nameSlug
// Big player, YouTube-title-matching name, license selector (Phase A:
// Buy buttons open a placeholder toast — real Stripe checkout ships in
// Phase B alongside the auto-account + signed-download flow).
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Pause, ArrowLeft, MusicNote, Info, Check, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../api";
import { useAuth } from "../auth/AuthContext";
import GlobalNav from "../components/GlobalNav";
import ProfileCircle from "../auth/ProfileCircle";

const TIERS = [
  {
    id: "mp3",
    label: "MP3 Lease",
    default_cents: 3000,
    included: ["Untagged MP3", "Basic license"],
  },
  {
    id: "wav",
    label: "WAV + Stems Lease",
    default_cents: 10000,
    included: ["Untagged WAV", "Track stems (zip)", "Untagged MP3", "Premium license"],
    popular: true,
  },
  {
    id: "exclusive",
    label: "Exclusive",
    default_cents: 25000,
    included: ["All files (WAV, stems, MP3)", "Full rights — beat removed from store"],
  },
];

// License comparison rows: [label, mp3, wav, exclusive]
const COMPARE_ROWS = [
  ["Untagged MP3", true, true, true],
  ["Untagged WAV (studio quality)", false, true, true],
  ["Track stems (zip)", false, true, true],
  ["License type", "Basic", "Premium", "Exclusive"],
  ["Streams", "Unlimited", "Unlimited", "Unlimited"],
  ["Commercial sales & distribution", true, true, true],
  ["Live performances for profit", true, true, true],
  ["Beat stays available to others", "Yes", "Yes", "No — it's yours"],
  ["Ownership", "Producer keeps copyright", "Producer keeps copyright", "Full rights transferred"],
];

const priceFmt = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

export default function BeatDetailPage() {
  const { artistSlug, nameSlug } = useParams();
  const nav = useNavigate();
  const [beat, setBeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tier, setTier] = useState("wav");
  const { user } = useAuth();
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get(`/store/beats/${artistSlug}/${nameSlug}`);
        setBeat(data.beat);
        try {
          const src = new URLSearchParams(window.location.search).get("utm_source") || "direct";
          API.post("/store/track", { event: "beat_page_view", beat_id: data.beat.id, utm_source: src }).catch(() => {});
        } catch { /* noop */ }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Beat not found");
        nav("/beats");
      } finally { setLoading(false); }
    })();
  }, [artistSlug, nameSlug, nav]);

  const togglePlay = () => {
    if (!beat) return;
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.src = `${process.env.REACT_APP_BACKEND_URL}${beat.preview_stream_url}`;
    a.play().then(() => setPlaying(true)).catch(() => toast.error("Preview unavailable"));
    a.onended = () => setPlaying(false);
  };

  const [busy, setBusy] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState(() => user?.email || "");
  useEffect(() => {
    if (user?.email) setBuyerEmail((cur) => cur || user.email);
  }, [user?.email]);
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(buyerEmail.trim());
  const onBuy = async () => {
    if (busy) return;
    // v19.40 — Email is REQUIRED before we hit Stripe. Backend also
    // enforces this; the UI gate just makes the requirement obvious.
    if (!emailValid) { toast.error("Enter a valid email to receive your files."); return; }
    // v19.33 — Stripe Checkout replaces the "DM the producer" fallback.
    // The button posts to /api/store/checkout which returns a Stripe
    // session URL; on paid completion, the webhook auto-creates the
    // buyer's account, builds the license PDF, emails the download
    // links, and (via /purchase/success/pending) redirects here.
    setBusy(true);
    try {
      API.post("/store/track", { event: "beat_checkout_started", beat_id: beat?.id, tier }).catch(() => {});
      const utm_source = new URLSearchParams(window.location.search).get("utm_source") || (() => {
        try { return JSON.parse(localStorage.getItem("crow_utm") || "null")?.utm_source || "direct"; } catch { return "direct"; }
      })();
      const utm_campaign = new URLSearchParams(window.location.search).get("utm_campaign") || null;
      const { data } = await API.post("/store/checkout", {
        beat_id: beat.id,
        tier,
        buyer_email: buyerEmail.trim().toLowerCase(),
        origin_url: window.location.origin,
        utm_source,
        utm_campaign,
      });
      if (!data?.url) throw new Error("Stripe did not return a checkout URL");
      // v19.41 — Refuse to redirect anywhere that isn't the real
      // hosted Stripe Checkout page. Prevents the class of bugs
      // where a misconfigured backend could redirect the buyer
      // straight to our success page without a payment ever
      // happening.
      if (!/^https:\/\/checkout\.stripe\.com\//.test(data.url)) {
        throw new Error("Unsafe checkout URL returned — refusing to redirect. Please contact support.");
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not start checkout");
      setBusy(false);
    }
  };

  if (loading || !beat) {
    return <div className="min-h-screen text-white flex items-center justify-center bg-black">Loading beat…</div>;
  }

  const priceOf = (id) => (beat.prices?.[id] ?? TIERS.find((t) => t.id === id)?.default_cents);

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(circle at 50% -10%, rgba(34,197,94,0.08), #000 55%)" }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between gap-3 mb-6">
          <button onClick={() => nav("/beats")} className="text-white/50 hover:text-white text-sm flex items-center gap-1" data-testid="beat-detail-back">
            <ArrowLeft size={16} weight="bold" /> All beats
          </button>
          <div className="flex items-center gap-2">
            <GlobalNav />
            <ProfileCircle signInPill />
          </div>
        </div>

        {/* Player card */}
        <div className="rounded-3xl bg-[#0a0a0a] border border-[#1f1f1f] p-6 sm:p-8 mb-6" data-testid="beat-detail-hero">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              onClick={togglePlay}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_36px_rgba(16,185,129,0.35)] shrink-0"
              data-testid="beat-detail-play"
              aria-label={playing ? "Pause preview" : "Play preview"}
            >
              {playing ? <Pause size={36} weight="fill" /> : <Play size={36} weight="fill" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-mono font-black text-3xl sm:text-4xl tracking-tight break-words">"{beat.beat_name}"</div>
              <div className="text-white/60 mt-1">{beat.artist_tag}</div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10 flex items-center gap-1"><MusicNote size={12} weight="bold" /> {beat.bpm} BPM</span>
                {beat.musical_key ? <span className="px-2 py-1 rounded-full bg-white/5 border border-white/10">{beat.musical_key}</span> : null}
                {(beat.tags || []).map((t) => (
                  <span key={t} className="px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200">#{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* License selector */}
        <div className="rounded-3xl bg-[#0a0a0a] border border-[#1f1f1f] p-6 sm:p-8">
          <div className="font-bold text-lg mb-4">Choose a license</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2" data-testid="beat-detail-license-grid">
            {TIERS.map((t) => {
              const active = tier === t.id;
              const disabled = t.id !== "exclusive" && beat.sold_exclusive; // once sold exclusive, only exclusive path — but page 410s anyway
              return (
                <button
                  key={t.id}
                  onClick={() => setTier(t.id)}
                  disabled={disabled}
                  className={`relative text-left rounded-2xl p-4 border transition ${active ? "border-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.35)]" : t.popular ? "border-yellow-400/50" : "border-white/10 hover:border-white/25"}`}
                  style={{ background: active ? "rgba(16,185,129,0.08)" : "rgba(255,255,255,0.02)" }}
                  data-testid={`license-${t.id}`}
                >
                  {t.popular && (
                    <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-yellow-300 text-black" data-testid={`license-${t.id}-popular-badge`}>
                      Most popular
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-bold text-sm">{t.label}</div>
                    <div className={`font-black text-xl ${active ? "text-emerald-300" : "text-white"}`}>{priceFmt(priceOf(t.id))}</div>
                  </div>
                  <ul className="mt-2 text-xs text-white/60 space-y-1">
                    {t.included.map((line) => (<li key={line}>· {line}</li>))}
                  </ul>
                </button>
              );
            })}
          </div>
          <LicenseCompareTable activeTier={tier} onPick={setTier} priceOf={priceOf} />
          <OrderSummary beat={beat} tier={TIERS.find((t) => t.id === tier)} cents={priceOf(tier)} />
          <div className="mt-5 space-y-2">
            <label htmlFor="buyer-email" className="block text-xs uppercase tracking-wide text-white/60 font-bold">
              Email for delivery — your files and license will be sent here.
            </label>
            {user?.email && buyerEmail === user.email ? (
              <div className="text-[11px] text-emerald-300/80" data-testid="beat-detail-email-prefilled">Prefilled from your account</div>
            ) : null}
            <input
              id="buyer-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={buyerEmail}
              onChange={(e) => setBuyerEmail(e.target.value)}
              className={`w-full px-3.5 py-3 rounded-xl bg-black/40 border text-sm text-white placeholder-white/30 outline-none transition ${
                buyerEmail && !emailValid
                  ? "border-red-400/60 focus:border-red-400"
                  : emailValid
                  ? "border-emerald-400/60 focus:border-emerald-300"
                  : "border-white/15 focus:border-white/40"
              }`}
              data-testid="beat-detail-buyer-email"
            />
            {buyerEmail && !emailValid ? (
              <div className="text-[11px] text-red-300">Enter a valid email address to receive your beat.</div>
            ) : null}
          </div>
          <button
            onClick={onBuy}
            disabled={busy || !emailValid}
            className="mt-3 w-full py-3 rounded-full font-black text-sm sm:text-base bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="beat-detail-buy"
          >{busy ? "Redirecting to secure checkout…" : !emailValid ? "Enter your email to buy" : `Checkout · ${priceFmt(priceOf(tier))} total`}</button>
          <div className="mt-2 text-center text-[11px] text-white/45" data-testid="beat-detail-payment-methods">
            Secure Stripe checkout · Card, Apple Pay, Google Pay, Link &amp; PayPal
          </div>
          <p className="mt-3 text-[11px] text-white/50 flex items-start gap-2">
            <Info size={12} weight="bold" className="mt-0.5 shrink-0" />
            Free for non-profit use (streaming only). A lease is required to release music on this beat.
          </p>
        </div>
      </div>
    </div>
  );
}

function Cell({ v }) {
  if (v === true) return <Check size={14} weight="bold" className="mx-auto text-emerald-300" />;
  if (v === false) return <Minus size={14} weight="bold" className="mx-auto text-white/25" />;
  return <span className="text-[11px] text-white/75">{v}</span>;
}

function LicenseCompareTable({ activeTier, onPick, priceOf }) {
  return (
    <div className="mt-6 rounded-2xl border border-white/10 overflow-x-auto" data-testid="license-compare-table">
      <table className="w-full text-xs min-w-[520px]">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left font-bold p-3 text-white/60">What you get</th>
            {TIERS.map((t) => (
              <th key={t.id} className="p-3 text-center">
                <button
                  onClick={() => onPick(t.id)}
                  className={`font-bold ${activeTier === t.id ? "text-emerald-300" : "text-white hover:text-emerald-200"}`}
                  data-testid={`compare-col-${t.id}`}
                >
                  {t.label}
                  <div className="font-black text-sm">{priceFmt(priceOf(t.id))}</div>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map(([label, ...vals]) => (
            <tr key={label} className="border-b border-white/5 last:border-0">
              <td className="p-3 text-white/70">{label}</td>
              {vals.map((v, i) => (
                <td key={i} className={`p-3 text-center ${activeTier === TIERS[i].id ? "bg-emerald-500/[0.06]" : ""}`}><Cell v={v} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrderSummary({ beat, tier, cents }) {
  return (
    <div className="mt-5 rounded-2xl bg-black/40 border border-white/10 p-4 text-sm" data-testid="order-summary">
      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Order summary</div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-white/80 truncate">"{beat.beat_name}" · {tier?.label}</span>
        <span className="font-bold">{priceFmt(cents)}</span>
      </div>
      <div className="flex items-center justify-between gap-3 text-white/50 text-xs mt-1">
        <span>Fees</span><span>$0</span>
      </div>
      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-white/10">
        <span className="font-bold">Total due today</span>
        <span className="font-black text-xl text-emerald-300" data-testid="order-summary-total">{priceFmt(cents)} USD</span>
      </div>
    </div>
  );
}
